import React, { useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { FieldDiamond } from './FieldDiamond';
import { images } from '../data/images';
type Page = {
  kind: string;
  caption: string;
  band: string;
};

/**
 * "Peek inside" — los tipos de página reales del pack (Índices DPS-33).
 * Cuando se carguen capturas reales de los tomos en data/images.ts → pages,
 * se muestran en lugar de la tarjeta dibujada.
 */
const pages: Page[] = [{
  kind: 'Volume cover',
  caption: 'Each volume owns one part of the game — Vol. 5: Shut Down the Inning',
  band: 'bg-gold'
}, {
  kind: '4-Block practice plan',
  caption: 'A complete practice you print and run as-is',
  band: 'bg-green'
}, {
  kind: 'Drill card',
  caption: 'Objective, time, gear, field diagram, common error and fix',
  band: 'bg-clay'
}, {
  kind: 'This vs. that',
  caption: 'Two options side by side, and when to use each one',
  band: 'bg-crimson'
}, {
  kind: 'Field diagram',
  caption: 'Who goes where, drawn on a real field with the coach cue',
  band: 'bg-green'
}, {
  kind: 'Field card',
  caption: 'One page per volume that lives on your clipboard',
  band: 'bg-gold'
}];
export function LookInside() {
  const trackRef = useRef<HTMLUListElement>(null);
  const [active, setActive] = useState(0);
  const handleScroll = () => {
    const el = trackRef.current;
    if (!el) return;
    if (el.scrollLeft >= el.scrollWidth - el.clientWidth - 4) {
      setActive(pages.length - 1);
      return;
    }
    const items = Array.from(el.children) as HTMLElement[];
    let closest = 0;
    items.forEach((item, i) => {
      if (Math.abs(item.offsetLeft - el.scrollLeft) < Math.abs(items[closest].offsetLeft - el.scrollLeft)) {
        closest = i;
      }
    });
    setActive(closest);
  };
  const goTo = (i: number) => {
    const el = trackRef.current;
    if (!el) return;
    const target = Math.max(0, Math.min(pages.length - 1, i));
    const item = el.children[target] as HTMLElement | undefined;
    if (item) el.scrollTo({
      left: item.offsetLeft,
      behavior: 'smooth'
    });
  };
  return <section aria-labelledby="peek-heading" className="w-full bg-ice" data-id="element-161" data-mp="1den02std1idc">
      <div className="max-w-6xl mx-auto px-5 pt-16 sm:pt-20" data-id="element-162" data-mp="1dfg9k1jpelz6">
        <div className="text-center" data-id="element-163" data-mp="1hf6xlxhuio00">
          <p className="text-[11px] font-bold uppercase tracking-[1.6px] text-gold-deep" data-id="element-164" data-mp="1hicnheyattws">
            A peek inside
          </p>
          <h2 id="peek-heading" className="chalk-underline mt-3 font-display font-bold uppercase text-ink text-4xl sm:text-5xl" data-id="element-165" data-mp="posp6gslq3vq">
            Built for the field, not the shelf
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-ink-soft" data-id="element-166" data-mp="yqtmfdnu4pxe">
            One page, one idea. No walls of text, no theory to wade through
            &mdash; every page is something you run at your next practice.
          </p>
        </div>

        <div className="relative mt-9" data-id="element-167" data-mp="2a6qpe5sidadm">
          <FieldDiamond className="pointer-events-none absolute -left-10 -top-6 hidden h-40 w-40 text-ink/[0.06] lg:block" data-id="element-168" data-mp="2k9435grk97wc" />
          <ul ref={trackRef} onScroll={handleScroll} aria-label="Sample pages" className="relative flex snap-x snap-mandatory gap-4 overflow-x-auto pb-5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-169" data-mp="2fab0kjdtb1ay">
            {pages.map((page, i) => {
            const src = images.pages[i];
            return <li key={page.kind} className="snap-start shrink-0 basis-[200px] sm:basis-[232px]" data-id="element-170" data-mp="1wj08hywcoihh" data-mpi={i}>
                  <figure className="clip-corner overflow-hidden rounded-2xl border border-ice-dark bg-white shadow-card" data-id="element-171" data-mp="1rm04rumz3rtj">
                    <div className="relative flex aspect-[9/16] w-full flex-col overflow-hidden bg-navy" data-id="element-172" data-mp="3lxy5t9b4phec">
                      {src ? <img src={src} alt={page.caption} loading="lazy" className="absolute inset-0 h-full w-full object-cover object-top" data-id="element-173" data-mp="3hxxlx959gdzw" /> : <>
                          <span className={`h-2 w-full ${page.band}`} data-id="element-174" data-mp="3g17dbpmk5ja1" />
                          <FieldDiamond className="pointer-events-none absolute -bottom-8 -right-8 h-40 w-40 text-ice/10" data-id="element-175" data-mp="3jz53auo5k9eh" />
                          <div className="relative flex flex-1 flex-col px-4 pt-9" data-id="element-176" data-mp="3678gn6596rph">
                            <span className="font-display text-[11px] tracking-[2px] text-gold" data-id="element-177" data-mp="3b1cn7oalwxq1">
                              Softball Season-Ready
                            </span>
                            <span className="mt-2 font-display text-3xl leading-[1] text-white" data-id="element-178" data-mp="31b05s4y39ko4">
                              {page.kind}
                            </span>
                            <span className="mt-4 grid gap-1.5" aria-hidden="true" data-id="element-179" data-mp="2s9tldksbjppl">
                              <span className="h-1.5 w-11/12 rounded-full bg-white/15" data-id="element-180" data-mp="28ni898ebtu9v" />
                              <span className="h-1.5 w-9/12 rounded-full bg-white/15" data-id="element-181" data-mp="23onjzqxlbdid" />
                              <span className="h-1.5 w-10/12 rounded-full bg-white/15" data-id="element-182" data-mp="23r09qq9las5z" />
                            </span>
                            <span className="mt-auto mb-5 h-4 w-4 rounded-full bg-gold" aria-hidden="true" data-id="element-183" data-mp="2dmbp9asto3es" />
                          </div>
                        </>}
                    </div>
                    <figcaption className="px-3.5 py-3 text-[12.5px] font-medium leading-snug text-ink" data-id="element-184" data-mp="2mob5i0w0j3go">
                      {page.caption}
                    </figcaption>
                  </figure>
                </li>;
          })}
          </ul>

          <div className="mt-1 flex items-center justify-center gap-5" data-id="element-185" data-mp="3ad3utei4qd2b">
            <button type="button" onClick={() => goTo(active - 1)} disabled={active === 0} aria-label="Previous page" className="flex h-11 w-11 items-center justify-center rounded-full border border-ice-dark bg-white text-ink shadow-card transition-opacity duration-150 ease-out disabled:opacity-30 focus:outline-none focus-visible:ring-2 focus-visible:ring-clay" data-id="element-186" data-mp="3addkt236ck80">
              <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-187" data-mp="2gsje80duhkqf" />
            </button>

            <div className="flex items-center gap-2" data-id="element-188" data-mp="2lrcoljxu4vmd">
              {pages.map((page, i) => <button key={page.kind} type="button" onClick={() => goTo(i)} aria-label={`Go to ${page.kind}`} aria-current={i === active} className={`h-2.5 rounded-full transition-[width,background-color] duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-clay ${i === active ? 'w-7 bg-ink' : 'w-2.5 bg-ink/20'}`} data-id="element-189" data-mp="2qpxheyarcd0s" data-mpi={i} />)}
            </div>

            <button type="button" onClick={() => goTo(active + 1)} disabled={active === pages.length - 1} aria-label="Next page" className="flex h-11 w-11 items-center justify-center rounded-full border border-ice-dark bg-white text-ink shadow-card transition-opacity duration-150 ease-out disabled:opacity-30 focus:outline-none focus-visible:ring-2 focus-visible:ring-clay" data-id="element-190" data-mp="xuwjb8y25ioi">
              <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-191" data-mp="1bsl5ql7rwxvo" />
            </button>
          </div>

          <p className="chalk-note mt-4 text-center text-xl text-ink-soft" data-id="element-192" data-mp="17qq2vca72cfs">
            43 practices · 50+ drills · 27 worksheets · 6 field cards
          </p>
        </div>
      </div>
    </section>;
}